import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  withStyles,
  Collapse,
  colors,
  IconButton,
  CircularProgress, Typography,
} from "@material-ui/core";
import React, {ReactNode, useState} from "react";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import ExpandMoreOutlined from "@material-ui/icons/ExpandMore";
import { FormattedMessage, useIntl } from "react-intl";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import {SortableValue} from "./PersonalAccountFilters";

export const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.white,
    color: colors.grey[800],
    fontSize: 15,
  },
  body: {
    fontSize: 14,
    textAlign: "center",
  },
}))(TableCell);

export const StyledTableRow = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
  },
}))(TableRow);

interface TableDataProps {
  header: ReactNode;
  rows: ReactNode;

  paginator?: TablePaginatorProps;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: "100%",
    },

    paginator: {
      paddingRight: 10,
    },

    table: {
      width: "100%",

      "& thead tr th": {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },

      "& tbody tr td": {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
    },
  }),
  { name: "table-data-styles" }
);

export default function TableData(props: TableDataProps) {
  const classes = useStyles(props);
  // Props
  const { rows, header, paginator, ...rest } = props;

  return (
    <Box className={classes.root} {...rest}>
      <Box className={classes.paginator}>
        {paginator ? <TablePaginator {...paginator} /> : <></>}
      </Box>
      <TableContainer component="div">
        <Table
          stickyHeader
          className={classes.table}
          aria-label="app sticky table"
        >
          <TableHead>{header}</TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface CollapseTableRowProps {
  collapse?: ReactNode;
  children?: ReactNode;
  colSpan: number;

  paginator?: TablePaginatorProps;
}

const useCollapseRowStyles = makeStyles(
  (theme) => ({
    root: {
      "& > *": {
        borderBottom: "unset",
      },
    },
  }),
  { name: "table-row-styles" }
);

export function CollapseTableRow(props: CollapseTableRowProps) {
  const classes = useCollapseRowStyles(props);
  // Props
  const { collapse, children, colSpan, ...rest } = props;
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment {...rest}>
      <StyledTableRow className={classes.root}>
        <StyledTableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
        {children}
      </StyledTableRow>
      <StyledTableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={colSpan + 1}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>{collapse}</Box>
          </Collapse>
        </TableCell>
      </StyledTableRow>
    </React.Fragment>
  );
}

const usePaginatorStyles = makeStyles(
  (theme) => ({
    root: {
      paddingBottom: theme.spacing(2),
    },

    wrapper: {
      width: "100%",
      paddingLeft: theme.spacing(4),
      paddingRight: 4,
    },

    actionsWrapper: {
      flexShrink: 0,
    },

    actions: {
      color: colors.grey[800],
    },

    actionsPrev: {
      transform: "rotate(90deg)",
      marginLeft: "auto",
      marginRight: 4,
      color: theme.palette.text.primary,
    },

    actionsNext: {
      transform: "rotate(-90deg)",
      marginLeft: "auto",
      color: theme.palette.text.primary,
    },

    titles: {
      fontSize: 13,
      color: colors.grey[800],
      marginRight: 20,
      width: "100%",
    },
  }),
  { name: "table-paginator-styles" }
);

interface TablePaginatorProps {
  page: number;
  totalPage: number;
  total: number;
  title: ReactNode;
  loading?: boolean;
  handlePrevAction?: () => void | null;
  handleNextAction?: () => void | null;
}

export function TablePaginator(props: TablePaginatorProps) {
  const classes = usePaginatorStyles(props);
  const intl = useIntl();
  // Props
  const {
    page,
    title,
    total,
    totalPage,
    handleNextAction,
    handlePrevAction,
    loading,
    ...rest
  } = props;

  return (
    <Box className={classes.root} {...rest}>
      <Box
        component="div"
        className={classes.wrapper}
        display="flex"
        alignItems="start"
      >
        <Box className={classes.titles}>{title}</Box>

        <Box
          className={classes.actionsWrapper}
          display="flex"
          alignItems="center"
          mt={1}
        >
          {loading ? (
            <Box display="inline-block" mr={4}>
              <CircularProgress color="secondary" size={16} thickness={4} />
            </Box>
          ) : (
            <></>
          )}
          <Box component="div">
            <Box component="div" className={classes.titles}>
              <FormattedMessage
                id="sharedScreen.paginatorDynamicPage"
                values={{ page: page, totalPage: totalPage }}
              />
            </Box>
          </Box>
          <Box className={classes.actions}>
            <IconButton
              className={classes.actionsPrev}
              onClick={handlePrevAction}
              disabled={page === 1}
            >
              <ExpandMoreOutlined
                fontSize="small"
                color={page === 1 ? "disabled" : "inherit"}
                titleAccess={intl.formatMessage({
                  id: "sharedScreen.previous",
                })}
              />
            </IconButton>
            <IconButton
              onClick={handleNextAction}
              className={classes.actionsNext}
              disabled={page === totalPage}
            >
              <ExpandMoreOutlined
                fontSize="small"
                color={page === totalPage ? "disabled" : "inherit"}
                titleAccess={intl.formatMessage({ id: "sharedScreen.next" })}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TablePaginator.defaultProps = {
  loading: false,
};

export const useSortableCellStyles = makeStyles(
    (theme) => ({
      root: {
        cursor: "pointer",
        "& .MuiSvgIcon-fontSizeSmall": {
          fontSize: "0.86rem !important",
        },
      },
    }),
    {
      name: "sortalable-cell-styles",
      flip: null,
    }
);

interface SortableCellProps {
  text: string;
  ontSort?: (sortBy: SortableValue) => void;
}

export const SortableCell = (props: SortableCellProps) => {
  const classes = useSortableCellStyles(props);
  const { text, ontSort } = props;
  // State
  const [sortBy, setSortBy] = useState((): SortableValue => "desc");

  const sortableCallback = () => {
    if (ontSort !== null && ontSort !== undefined) {
      let newSortBy: SortableValue = sortBy === "desc" ? "asc" : "desc";

      ontSort(newSortBy);
      setSortBy(newSortBy);
    }
  };

  return (
      <Box className={classes.root} onClick={(_) => sortableCallback()}>
        <Box display="flex" alignItems="center">
          <Box style={{ flexGrow: 1 }}>
            <Typography component="span" noWrap>
              <FormattedMessage id={text} />
            </Typography>
          </Box>
          {sortBy === "desc" ? (
              <ArrowUpwardIcon fontSize="small" />
          ) : (
              <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </Box>
  );
};

SortableCell.defaultProps = {
  sortBy: "desc"
};